/**
 * Integrations Hooks
 *
 * React Query hooks for fetching and managing platform integrations.
 * Data is loaded from the Platform Admin API (port 9302).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { integrationsApi, type Integration } from '../api/client';

const QUERY_KEY = 'integrations';

/**
 * Hook for listing integrations (mixed -- third-party integrations + Aaim services).
 * Use useThirdPartyIntegrations() and useAaimServices() instead when the page
 * needs to separate the two categories (Sprint C17).
 */
export function useIntegrations() {
  return useQuery<Integration[], Error>({
    queryKey: [QUERY_KEY],
    queryFn: () => integrationsApi.list(),
    refetchInterval: 60000,
  });
}

/**
 * Hook for listing third-party (non-Aaim) integrations. Backs the
 * Integrations page (Sprint C17 rename of Data Providers).
 */
export function useThirdPartyIntegrations() {
  return useQuery<Integration[], Error>({
    queryKey: [QUERY_KEY, 'third-party'],
    queryFn: () => integrationsApi.listThirdParty(),
    refetchInterval: 60000,
  });
}

/**
 * Hook for listing internal Aaim service integrations. Backs the
 * Aaim Services page (Sprint C17).
 */
export function useAaimServices() {
  return useQuery<Integration[], Error>({
    queryKey: [QUERY_KEY, 'aaim-services'],
    queryFn: () => integrationsApi.listAaimServices(),
    refetchInterval: 60000,
  });
}

/**
 * Hook for testing an integration connection.
 * Returns the test result with latency and success/failure status.
 */
export function useTestIntegration() {
  return useMutation<
    { success: boolean; latencyMs: number; error?: string; details?: Record<string, unknown> },
    Error,
    string
  >({
    mutationFn: (integrationId: string) => integrationsApi.testConnection(integrationId),
  });
}

/**
 * Hook for toggling an integration enabled state.
 * Invalidates the integrations list on success.
 *
 * No hook writes an integration's config blob: the unwired config writer was
 * deleted by operator ruling (2026-09-27, unwired-deletions-r3). Credential
 * material goes through providerCredentialApi, and a declared config field
 * gets its own hook.
 */
export function useToggleIntegration() {
  const queryClient = useQueryClient();

  return useMutation<Integration, Error, { id: string; enabled: boolean }>({
    mutationFn: ({ id, enabled }) => integrationsApi.update(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
